<template>
  <div class="h5-container">
    <!-- 顶部导航栏 -->
    <van-nav-bar
      class="top-bar"
      :title="totalUnread > 0 ? `消息(${totalUnread})` : '消息'"
      left-text="返回"
      left-arrow
      @click-left="goBack"
    >
      <template #right>
        <span class="status" :class="{ online: wsConnected }">{{ wsConnected ? '在线' : '离线' }}</span>
      </template>
    </van-nav-bar>

    <!-- 会话列表 -->
    <div class="conversation-list">
      <div
        v-for="item in conversationList"
        :key="item.conversationKey"
        class="conversation-item"
        @click="selectConversation(item)"
      >
        <div class="avatar-wrap">
          <div class="avatar">
            <van-icon name="user-o" size="22" color="#D4AF37" />
          </div>
          <span class="online-dot" :class="{ online: item.online }"></span>
        </div>
        <div class="info">
          <div class="top-row">
            <span class="name">{{ item.otherNickname || '用户' + item.otherId }}</span>
            <span class="time">{{ formatTime(item.lastTime) }}</span>
          </div>
          <div class="bottom-row">
            <span class="last-msg">{{ item.lastContent || '暂无消息' }}</span>
            <van-badge
              v-if="item.unreadCount > 0"
              :content="item.unreadCount > 99 ? '99+' : item.unreadCount"
              class="unread-badge"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div class="empty-tip" v-if="conversationList.length === 0">
      <van-icon name="chat-o" size="56" color="#444" />
      <p>暂无会话</p>
    </div>
  </div>
</template>

<script>
import { memberInformation } from '@/api/mine'
import { getConversationList } from '@/api/chat'

export default {
  name: 'DlsMessage',
  data() {
    return {
      adminId: '',
      userType: 2, // 代理商/内勤在 chat 协议中是服务端：1
      ws: null,
      wsConnected: false,
      heartbeatTimer: null,
      heartbeatTimeoutTimer: null,
      lastReceivedTime: 0,
      reconnectTimer: null,
      reconnectCount: 0,
      conversationList: [],
      refreshTimer: null
    }
  },
  computed: {
    totalUnread() {
      return this.conversationList.reduce((sum, item) => sum + (item.unreadCount || 0), 0)
    }
  },
  created() {
    this.init()
  },
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  activated() {
    // keep-alive 重新激活时刷新列表 + 重连 WS
    this.loadConversationList(true)
    this.connectWebSocket()
    this.startRefreshTimer()
  },
  deactivated() {
    this.stopRefreshTimer()
    this.destroyWebSocket()
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.stopRefreshTimer()
    this.destroyWebSocket()
  },
  methods: {
    async init() {
      await this.getUserInfo()
      if (!this.adminId) return
      await this.loadConversationList()
      this.connectWebSocket()
      this.startRefreshTimer()
    },
    async getUserInfo() {
      try {
        const res = await memberInformation()
        if (res.code === 200 && res.data) {
          this.adminId = String(res.data.id || res.data.userId || '')
        }
      } catch (e) {
        console.error('获取用户信息失败', e)
      }
    },

    // ===== 会话列表 =====
    async loadConversationList(silent = false) {
      if (!this.adminId) return
      try {
        const res = await getConversationList(this.adminId, this.userType)
        if (res.code === 200) {
          this.conversationList = res.data || []
        }
      } catch (e) {
        if (!silent) console.error('加载会话列表失败', e)
      }
    },
    // 定时刷新会话列表（兜底，防止 WS 消息丢失）
    startRefreshTimer() {
      this.stopRefreshTimer()
      this.refreshTimer = setInterval(() => {
        this.loadConversationList(true)
      }, 30000)
    },
    stopRefreshTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    // 选择会话 → 跳转聊天页
    selectConversation(item) {
      console.log(149,item)
      // 进入前清除该会话的未读数
      item.unreadCount = 0
      this.$router.push({
        path: '/app_dls_chat',
        query: {
          conversationKey: item.conversationKey || '',
          otherId: String(item.otherId || ''),
          otherNickname: item.otherNickname || '',
          otherUserType: 2,
          online: item.online ? '1' : '0'
        }
      })
    },

    // ===== WebSocket =====
    buildWsUrl() {
      if (process.env.NODE_ENV === 'development') {
        return `ws://${window.location.hostname}:8080`
      }
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${protocol}//${window.location.host}`
    },
    connectWebSocket() {
      if (!this.adminId) return
      if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return
      const fullUrl = `${this.buildWsUrl()}/ws/chat/${this.adminId}/${this.userType}`
      try {
        this.ws = new WebSocket(fullUrl)
        this.ws.binaryType = 'arraybuffer'

        this.ws.onopen = () => {
          this.wsConnected = true
          this.reconnectCount = 0
          this.lastReceivedTime = Date.now()
          this.startHeartbeat()
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
          }
          // 重连成功后立即刷新会话列表，校准断开期间的未读数
          this.loadConversationList(true)
        }

        this.ws.onmessage = async (event) => {
          this.lastReceivedTime = Date.now()
          this.clearHeartbeatTimeout()

          let data = event.data
          if (data instanceof Blob) {
            try { data = await data.text() } catch (e) { return }
          }
          if (data instanceof ArrayBuffer) {
            try { data = new TextDecoder('utf-8').decode(data) } catch (e) { return }
          }
          let msg
          try {
            msg = JSON.parse(data)
          } catch (e) {
            console.error('消息JSON解析失败', e, data)
            return
          }
          if (msg.msgType === 'heartbeat') return
          this.handleReceiveMessage(msg)
        }

        this.ws.onclose = () => {
          this.wsConnected = false
          this.stopHeartbeat()
          this.clearHeartbeatTimeout()
          this.autoReconnect()
        }

        this.ws.onerror = () => {
          // 交给 onclose 处理
        }
      } catch (e) {
        this.wsConnected = false
        this.autoReconnect()
      }
    },
    // 处理收到的消息：列表页收到消息一律更新会话列表 + 累加未读
    handleReceiveMessage(msg) {
      this.updateConversationLastMsg(msg, true)
    },
    // 更新会话列表的最后一条消息
    updateConversationLastMsg(msg, increaseUnread = false) {
      const conv = this.conversationList.find(c => c.conversationKey === msg.conversationKey)
      if (conv) {
        conv.lastContent = msg.content
        conv.lastTime = msg.createTime
        if (increaseUnread && String(msg.fromId) !== String(this.adminId)) {
          conv.unreadCount = (conv.unreadCount || 0) + 1
        }
        // 移到最前面
        const idx = this.conversationList.indexOf(conv)
        if (idx > 0) {
          this.conversationList.splice(idx, 1)
          this.conversationList.unshift(conv)
        }
      } else {
        // 新会话，插入到列表最前面
        this.conversationList.unshift({
          conversationKey: msg.conversationKey,
          otherId: msg.fromId,
          otherUserType: msg.fromUserType,
          otherNickname: msg.fromNickname || '用户' + msg.fromId,
          lastContent: msg.content,
          lastTime: msg.createTime,
          unreadCount: increaseUnread ? 1 : 0,
          online: true
        })
      }
    },
    // 心跳
    startHeartbeat() {
      this.stopHeartbeat()
      this.heartbeatTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ msgType: 'heartbeat' }))
          this.clearHeartbeatTimeout()
          // 10 秒内没收到任何消息（含心跳回复）则判定连接假死，主动关闭触发重连
          this.heartbeatTimeoutTimer = setTimeout(() => {
            if (this.ws) {
              try { this.ws.close() } catch (e) {}
            }
          }, 10000)
        }
      }, 30000)
    },
    clearHeartbeatTimeout() {
      if (this.heartbeatTimeoutTimer) {
        clearTimeout(this.heartbeatTimeoutTimer)
        this.heartbeatTimeoutTimer = null
      }
    },
    stopHeartbeat() {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer)
        this.heartbeatTimer = null
      }
      this.clearHeartbeatTimeout()
    },
    autoReconnect() {
      if (this.reconnectTimer) return
      this.reconnectCount++
      const delay = Math.min(this.reconnectCount * 3000, 30000)
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null
        this.connectWebSocket()
      }, delay)
    },
    handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return
      if (!this.adminId) return
      if (!this.ws || this.ws.readyState === WebSocket.CLOSING || this.ws.readyState === WebSocket.CLOSED) {
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer)
          this.reconnectTimer = null
        }
        this.reconnectCount = 0
        this.connectWebSocket()
        return
      }
      if (this.lastReceivedTime && Date.now() - this.lastReceivedTime > 30000) {
        try { this.ws.close() } catch (e) {}
      }
    },
    destroyWebSocket() {
      this.stopHeartbeat()
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      if (this.ws) {
        try { this.ws.close() } catch (e) {}
        this.ws = null
      }
      this.wsConnected = false
    },

    // ===== 工具 =====
    goBack() {
      this.$router.replace('/app_home')
      // this.$router.back()
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      if (date.toDateString() === now.toDateString()) {
        return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
      }
      return `${date.getMonth() + 1}/${date.getDate()}`
    }
  }
}
</script>

<style scoped lang="scss">
.h5-container {
  min-height: 100vh;
  background-color: #1A1A1A;
  padding-bottom: 60px;
}

.top-bar {
  background: linear-gradient(135deg, #D4AF37 0%, #B8860B 100%);

  ::v-deep .van-nav-bar__title {
    color: #0f0f0f;
    font-weight: 600;
  }
  ::v-deep .van-nav-bar__text {
    color: #0f0f0f;
  }
  ::v-deep .van-icon-arrow-left {
    color: #0f0f0f;
  }

  .status {
    font-size: 12px;
    color: #555;
    &.online {
      color: #52c41a;
    }
  }
}

.conversation-list {
  margin-top: 10px;
  background: #222;
}

.conversation-item {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #2a2a2a;

  &:active {
    background: #2a2a2a;
  }

  .avatar-wrap {
    position: relative;
    margin-right: 12px;
    flex-shrink: 0;

    .avatar {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #2a2a2a;
      border: 1px solid #D4AF37;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .online-dot {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #555;
      border: 2px solid #1A1A1A;

      &.online {
        background: #52c41a;
      }
    }
  }

  .info {
    flex: 1;
    min-width: 0;

    .top-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;

      .name {
        font-size: 15px;
        font-weight: 500;
        color: #fff;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 60%;
      }
      .time {
        font-size: 12px;
        color: #666;
        flex-shrink: 0;
        margin-left: 8px;
      }
    }

    .bottom-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .last-msg {
        font-size: 13px;
        color: #888;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        margin-right: 8px;
      }
    }
  }
}

.empty-tip {
  text-align: center;
  padding: 80px 20px;
  color: #555;

  p {
    margin-top: 16px;
    font-size: 14px;
  }
}

/* PC适配：参考 home.vue / mine.vue 约定，放大头像、文字，留出底部导航避让 */
@media screen and (min-width: 768px) {
  .h5-container {
    min-height: 100vh; // 覆盖全局110vh，列表页无需过长
    padding-bottom: 100px; // PC端底部导航更高（80px），留出避让
    box-shadow: 0 0 24px rgba(0, 0, 0, 0.5);
  }

  .conversation-list {
    margin-top: 16px;
  }

  .conversation-item {
    padding: 18px 20px;

    &:hover {
      background: #2a2a2a;
    }

    .avatar-wrap {
      margin-right: 16px;

      .avatar {
        width: 60px;
        height: 60px;
      }

      .online-dot {
        width: 14px;
        height: 14px;
        border-width: 3px;
      }
    }

    .info {
      .top-row {
        margin-bottom: 8px;

        .name {
          font-size: 18px;
        }
        .time {
          font-size: 14px;
        }
      }

      .bottom-row {
        .last-msg {
          font-size: 15px;
        }
      }
    }
  }

  .empty-tip {
    padding: 120px 20px;
  }
}
</style>
